import { CookiesProvider } from "react-cookie";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";

import { SocketProvider } from "./contexts/SocketContext";
import { TemplateProvider } from "./hooks/useTemplate";
import { useLegacyDomainRedirect } from "./hooks/useLegacyDomainRedirect";
import AuthMiddleware from "./middlewares/AuthMiddleware";
import GuestMiddleware from "./middlewares/GuestMiddleware";
import DefaultView from "./pages";
import MontageMeetingStatusesView from "@/pages/montageMeetingStatuses";
import ActivitySectorsView from "./pages/activities";
import DashboardView from "./pages/admin/dashboard";
import ForgotPasswordView from "./pages/auth/forgotPassword";
import LoginView from "./pages/auth/login";
import RegisterView from "./pages/auth/register";
import ResetForgotPasswordView from "./pages/auth/resetForgotPassword";
import CalendarView from "./pages/calendar";
import CalendarMontageView from "./pages/calendarMontage";
import CalendarVisibilitiesView from "./pages/calendarVisibilities";
import CallLogsView from "./pages/callLogs";
import ChangeLogView from "./pages/changelog";
import CompaniesView from "./pages/companies";
import CompanyView from "./pages/companies/[id]";
import CompanyCourseView from "./pages/companies/[id]/courses/[id]";
import CompanyCourseEditView from "./pages/companies/[id]/courses/[id]/edit";
import PreformatifDiagnosticView from "./pages/companies/[id]/courses/[id]/preformatif-diagnostic";
import CompanyEditView from "./pages/companies/[id]/edit";
import CompanyCourseStatusesView from "./pages/companyCourseStatuses";
import CompanyCourseStatusView from "./pages/companyCourseStatuses/[id]";
import CompanyCourseStatusEditView from "./pages/companyCourseStatuses/[id]/edit";
import CompanyStatusesView from "./pages/companyStatuses";
import CompanyStatusView from "./pages/companyStatuses/[id]";
import CompanyStatusEditView from "./pages/companyStatuses/[id]/edit";
import CoursesView from "./pages/courses";
import CourseView from "./pages/courses/[id]";
import CourseEditView from "./pages/courses/[id]/edit";
import EmailTemplatesView from "./pages/emailTemplates";
import GeneralView from "./pages/general";
import InstructorsView from "./pages/instructors";
import InstructorView from "./pages/instructors/[id]";
import InstructorEditView from "./pages/instructors/[id]/edit";
import OpcosView from "./pages/opcos";
import OrganisationsView from "./pages/organisations";
import OrganisationEditView from "./pages/organisations/[id]/edit";
import SignedConventionStatsView from "./pages/admin/signed-conventions-stats";
import SoftphoneLinesView from "./pages/softphoneLines";
import PeopleView from "./pages/people";
import PersonView from "./pages/people/[id]";
import PersonEditView from "./pages/people/[id]/edit";
import TrainingBrochuresView from "./pages/trainingBrochures";
import TeamsView from "./pages/teams";
import TeamView from "./pages/teams/[id]";
import TeamEditView from "./pages/teams/[id]/edit";
import UsersView from "./pages/users";
import UserView from "./pages/users/[id]";
import UserEditView from "./pages/users/[id]/edit";
import { store } from "./store";
import "./styles/scss/theme.scss";

import GoogleMeetSettings from "./pages/admin/googleMeet";
import ChatPage from "./pages/chat";

const LegacyDomainRedirect = () => {
  useLegacyDomainRedirect();
  return null;
};

const router = createBrowserRouter([
  {
    path: "/auth",
    element: <GuestMiddleware />,
    children: [
      {
        path: "login",
        element: <LoginView />,
      },
      {
        path: "register",
        element: <RegisterView />,
      },
      {
        path: "forgotPassword",
        element: <ForgotPasswordView />,
      },
      {
        path: "resetForgotPassword/:email",
        element: <ResetForgotPasswordView />,
      },
    ],
  },
  {
    path: "*",
    element: <AuthMiddleware />,
    children: [
      {
        index: true,
        element: <DefaultView />,
      },
      {
        path: "chat/:conversationId?",
        element: <ChatPage />,
      },
      {
        path: "montage-meeting-statuses",
        element: <MontageMeetingStatusesView />,
      },
      {
        path: "users",
        element: <UsersView />,
      },
      {
        path: "teams",
        element: <TeamsView />,
      },
      {
        path: "teams/:id",
        element: <TeamView />,
      },
      {
        path: "teams/:id/edit",
        element: <TeamEditView />,
      },
      {
        path: "users/:id",
        element: <UserView />,
      },
      {
        path: "users/:id/edit",
        element: <UserEditView />,
      },
      {
        path: "company-statuses",
        element: <CompanyStatusesView />,
      },
      {
        path: "company-statuses/:id",
        element: <CompanyStatusView />,
      },
      {
        path: "company-statuses/:id/edit",
        element: <CompanyStatusEditView />,
      },
      {
        path: "company-course-statuses",
        element: <CompanyCourseStatusesView />,
      },
      {
        path: "company-course-statuses/:id",
        element: <CompanyCourseStatusView />,
      },
      {
        path: "company-course-statuses/:id/edit",
        element: <CompanyCourseStatusEditView />,
      },
      {
        path: "opcos",
        element: <OpcosView />,
      },
      {
        path: "organizations",
        element: <OrganisationsView />,
      },
      {
        path: "organizations/:id/edit",
        element: <OrganisationEditView />,
      },
      {
        path: "email-templates",
        element: <EmailTemplatesView />,
      },
      {
        path: "activity-sectors",
        element: <ActivitySectorsView />,
      },
      {
        path: "people",
        element: <PeopleView />,
      },
      {
        path: "people/:id",
        element: <PersonView />,
      },
      {
        path: "people/:id/edit",
        element: <PersonEditView />,
      },
      {
        path: "courses",
        element: <CoursesView />,
      },
      {
        path: "courses/:id",
        element: <CourseView />,
      },
      {
        path: "courses/:id/edit",
        element: <CourseEditView />,
      },
      {
        path: "companies",
        element: <CompaniesView />,
      },
      {
        path: "general",
        element: <GeneralView />,
      },
      {
        path: "companies/:id/courses/:courseId",
        element: <CompanyCourseView />,
      },
      {
        path: "companies/:id/courses/:courseId/edit",
        element: <CompanyCourseEditView />,
      },
      {
        path: "companies/:id/courses/:courseId/preformatif-diagnostic",
        element: <PreformatifDiagnosticView />,
      },
      {
        path: "instructors",
        element: <InstructorsView />,
      },
      {
        path: "companies/:id",
        element: <CompanyView />,
      },
      {
        path: "companies/:id/edit",
        element: <CompanyEditView />,
      },
      {
        path: "calendar",
        element: <CalendarView />,
      },
      {
        path: "calendar-montage",
        element: <CalendarMontageView />,
      },
      {
        path: "calendar-visibilities",
        element: <CalendarVisibilitiesView />,
      },
      {
        path: "changelog",
        element: <ChangeLogView />,
      },
      {
        path: "instructors/:id",
        element: <InstructorView />,
      },
      {
        path: "instructors/:id/edit",
        element: <InstructorEditView />,
      },
      {
        path: "plaquettes",
        element: <TrainingBrochuresView />,
      },
      {
        path: "admin/signed-conventions-stats",
        element: <SignedConventionStatsView />,
      },
      {
        path: "admin/google-meet",
        element: <GoogleMeetSettings />,
      },
      {
        path: "admin/dashboard",
        element: <DashboardView />,
      },
      {
        path: "softphone-lines",
        element: <SoftphoneLinesView />,
      },
      {
        path: "call-logs",
        element: <CallLogsView />,
      },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <Provider store={store}>
    <TemplateProvider>
      <LegacyDomainRedirect />
      <CookiesProvider>
        <SocketProvider>
          <RouterProvider router={router} />
          <ToastContainer position="top-center" />
        </SocketProvider>
      </CookiesProvider>
    </TemplateProvider>
  </Provider>
);
